import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  AlertTriangle, 
  Mail, 
  Loader2, 
  ChevronDown, 
  ChevronRight,
  UserCheck,
  Trash2,
  RefreshCw,
  Calendar,
  Shield,
  Merge,
  CheckCircle,
  Bell,
  History,
  FileText,
  ArrowRight
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/modal-dialog";
import { formatDistanceToNow } from 'date-fns';

const API = import.meta.env.VITE_API_URL || '';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const DuplicateAccountsManager = () => {
  const [loading, setLoading] = useState(true);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [totalDuplicateUsers, setTotalDuplicateUsers] = useState(0);
  const [totalMergedAccounts, setTotalMergedAccounts] = useState(0);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [merging, setMerging] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState({});
  const [recentMerges, setRecentMerges] = useState([]);
  const [showMergeHistory, setShowMergeHistory] = useState(false);
  const [mergedAccountsList, setMergedAccountsList] = useState([]);
  const [showMergedAccounts, setShowMergedAccounts] = useState(false);
  const [loadingMergedAccounts, setLoadingMergedAccounts] = useState(false);
  const [sendingEntries, setSendingEntries] = useState(null); // Track which account we're sending entries for
  
  // Merge confirmation dialog state
  const [mergeConfirmDialog, setMergeConfirmDialog] = useState({
    isOpen: false,
    groupName: null,
    users: [],
    primaryUser: null,
    duplicateIds: [],
    totalEntries: 0
  });

  const fetchMergedAccountsList = async () => {
    setLoadingMergedAccounts(true);
    try {
      const res = await axios.get(`${API}/api/admin/merged-accounts`, {
        headers: { 'X-Admin-Token': getCookie('admin_token') }
      });
      setMergedAccountsList(res.data.merged_accounts || []);
    } catch (error) {
      console.error('Error fetching merged accounts:', error);
      toast.error('Failed to load merged accounts list');
    } finally {
      setLoadingMergedAccounts(false);
    }
  };

  const sendMergedEntriesToUser = async (mergedUserId, mergedEmail) => {
    setSendingEntries(mergedUserId);
    try {
      const res = await axios.post(
        `${API}/api/admin/merged-accounts/send-entries`,
        { merged_user_id: mergedUserId },
        { headers: { 'X-Admin-Token': getCookie('admin_token') } }
      );
      
      if (res.data.entries_found === 0) {
        toast.info(`No entries found for ${mergedEmail}. User has been notified.`);
      } else {
        toast.success(`Sent ${res.data.entries_found} entries to ${res.data.sent_to}`);
      }
    } catch (error) {
      console.error('Error sending merged entries:', error);
      toast.error(error.response?.data?.detail || 'Failed to send entries');
    } finally {
      setSendingEntries(null);
    }
  };

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/duplicate-accounts`, {
        headers: { 'X-Admin-Token': getCookie('admin_token') }
      });
      const groups = res.data.duplicate_groups || [];
      setDuplicateGroups(groups);
      setTotalGroups(res.data.total_groups || 0);
      setTotalDuplicateUsers(res.data.total_duplicate_users || 0);
      setTotalMergedAccounts(res.data.total_merged_accounts || 0);
      setRecentMerges(res.data.recent_merges || []);
      
      // Auto-select confirmed users as primary
      const autoSelected = {};
      groups.forEach(group => {
        const confirmedUser = group.users.find(u => u.duplicate_account_confirmed);
        if (confirmedUser) {
          autoSelected[group.name] = confirmedUser.user_id;
        }
      });
      setSelectedPrimary(prev => ({ ...prev, ...autoSelected }));
      
    } catch (error) {
      console.error('Error fetching duplicates:', error);
      toast.error('Failed to scan for duplicate accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const handleSendEmails = async (groupName, users) => {
    setSendingEmail(groupName);
    try {
      const userIds = users.map(u => u.user_id);
      const res = await axios.post(
        `${API}/api/admin/duplicate-accounts/send-email`,
        { user_ids: userIds },
        { headers: { 'X-Admin-Token': getCookie('admin_token') } }
      );
      
      if (res.data.success) {
        const successMsg = res.data.sent_count > 0 
          ? `Sent ${res.data.sent_count} email${res.data.sent_count > 1 ? 's' : ''} to duplicate account holders`
          : 'No emails were sent (users may not have valid email addresses)';
        
        if (res.data.failed_count > 0) {
          toast.warning(`${successMsg}. ${res.data.failed_count} failed.`);
        } else {
          toast.success(successMsg);
        }
        fetchDuplicates(); // Refresh to show notified status
      } else {
        toast.error('Email sending failed - please check backend logs');
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send emails. Check if email service is configured.';
      toast.error(errorMsg);
    } finally {
      setSendingEmail(null);
    }
  };

  const handleMerge = async (groupName, users) => {
    const primaryId = selectedPrimary[groupName];
    if (!primaryId) {
      toast.error('Please select a primary account to keep');
      return;
    }
    
    const duplicateIds = users.filter(u => u.user_id !== primaryId).map(u => u.user_id);
    const primaryUser = users.find(u => u.user_id === primaryId);
    const totalEntries = users.filter(u => u.user_id !== primaryId).reduce((sum, u) => sum + (u.journal_entry_count || 0), 0);
    
    // Show branded confirmation dialog instead of browser confirm
    setMergeConfirmDialog({
      isOpen: true,
      groupName,
      users,
      primaryUser,
      duplicateIds,
      totalEntries
    });
  };
  
  // Execute the actual merge after confirmation
  const executeMerge = async () => {
    const { groupName, primaryUser, duplicateIds } = mergeConfirmDialog;
    
    setMergeConfirmDialog(prev => ({ ...prev, isOpen: false }));
    setMerging(groupName);
    
    try {
      const res = await axios.post(
        `${API}/api/admin/duplicate-accounts/merge`,
        { 
          primary_user_id: primaryUser.user_id,
          duplicate_user_ids: duplicateIds
        },
        { headers: { 'X-Admin-Token': getCookie('admin_token') } }
      );
      
      if (res.data.success) {
        const entriesMsg = res.data.entries_transferred > 0 
          ? ` ${res.data.entries_transferred} journal entries transferred to ${res.data.primary_email}.`
          : '';
        toast.success(`Successfully merged ${res.data.merged_count} account(s)!${entriesMsg}`, {
          duration: 5000
        });
        fetchDuplicates(); // Refresh list
      }
    } catch (error) {
      console.error('Error merging accounts:', error);
      toast.error('Failed to merge accounts');
    } finally {
      setMerging(null);
    }
  };

  // Get groups that have users who confirmed their accounts (ready to merge)
  const pendingMergeGroups = duplicateGroups.filter(group => 
    group.users.some(u => u.duplicate_account_confirmed)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
        <span className="ml-3 text-white/60">Scanning for duplicate accounts...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PROMINENT ACTION REQUIRED ALERT */}
      {pendingMergeGroups.length > 0 && (
        <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 rounded-xl p-5 animate-pulse-slow">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/30 flex items-center justify-center flex-shrink-0">
              <Bell className="w-6 h-6 text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-green-400 font-bold text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {pendingMergeGroups.length} User{pendingMergeGroups.length > 1 ? 's' : ''} Confirmed - Ready to Merge!
              </h3>
              <p className="text-green-200/80 mt-1">
                The following users clicked &ldquo;Keep This Account&rdquo; and confirmed which account they want to use. 
                You can now merge their duplicate accounts and transfer all journal entries.
              </p>
              
              <div className="mt-4 space-y-2">
                {pendingMergeGroups.map((group, idx) => {
                  const confirmedUser = group.users.find(u => u.duplicate_account_confirmed);
                  const otherAccounts = group.users.filter(u => !u.duplicate_account_confirmed);
                  const totalEntries = otherAccounts.reduce((sum, u) => sum + (u.journal_entry_count || 0), 0);
                  
                  return (
                    <div 
                      key={idx}
                      className="bg-[#1A0326]/80 border border-green-500/30 rounded-lg p-4 flex items-center justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{group.name}</span>
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                            Confirmed: {confirmedUser?.email}
                          </span>
                        </div>
                        <p className="text-sm text-white/50 mt-1">
                          {otherAccounts.length} duplicate account{otherAccounts.length > 1 ? 's' : ''} to merge
                          {totalEntries > 0 && ` • ${totalEntries} journal entries to transfer`}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleMerge(group.name, group.users)}
                        disabled={merging === group.name}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {merging === group.name ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Merge className="w-4 h-4 mr-2" />
                        )}
                        Merge Now
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-[#df1283]" />
              <span className="text-xs text-white/60">Duplicate Groups</span>
            </div>
            <div className="text-2xl font-bold text-white">{totalGroups}</div>
          </div>
          
          <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-white/60">Total Affected Users</span>
            </div>
            <div className="text-2xl font-bold text-amber-400">{totalDuplicateUsers}</div>
          </div>
          
          {/* Accounts Merged stat - clickable to view details */}
          <div 
            className="bg-[#0FFFE2]/10 rounded-xl p-4 border border-[#0FFFE2]/30 cursor-pointer hover:bg-[#0FFFE2]/20 transition-colors"
            onClick={() => {
              if (totalMergedAccounts > 0) {
                setShowMergedAccounts(!showMergedAccounts);
                if (!showMergedAccounts && mergedAccountsList.length === 0) {
                  fetchMergedAccountsList();
                }
              }
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Merge className="w-4 h-4 text-[#0FFFE2]" />
              <span className="text-xs text-[#0FFFE2]/60">Accounts Merged</span>
              {totalMergedAccounts > 0 && (
                <span className="text-xs text-[#0FFFE2]/40 ml-auto">Click to view</span>
              )}
            </div>
            <div className="text-2xl font-bold text-[#0FFFE2]">{totalMergedAccounts}</div>
          </div>
          
          {pendingMergeGroups.length > 0 && (
            <div className="bg-green-500/20 rounded-xl p-4 border border-green-500/50">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-xs text-green-300/60">Ready to Merge</span>
              </div>
              <div className="text-2xl font-bold text-green-400">{pendingMergeGroups.length}</div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {recentMerges.length > 0 && (
            <Button
              onClick={() => setShowMergeHistory(!showMergeHistory)}
              variant="outline"
              className={`border-[#4A0A66] text-white hover:bg-[#4A0A66]/30 ${showMergeHistory ? 'bg-[#4A0A66]/30' : ''}`}
            >
              <History className="w-4 h-4 mr-2" />
              Merge History ({recentMerges.length})
            </Button>
          )}
          <Button
            onClick={fetchDuplicates}
            variant="outline"
            className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Rescan
          </Button>
        </div>
      </div>

      {/* Merge History Section */}
      {showMergeHistory && recentMerges.length > 0 && (
        <div className="bg-[#1A0326] border border-[#4A0A66] rounded-xl p-4">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-[#0FFFE2]" />
            Recent Merge History
          </h4>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {recentMerges.map((merge, idx) => (
              <div key={idx} className="bg-[#4A0A66]/20 border border-[#4A0A66]/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium text-sm">
                    {merge.primary_account?.name || 'Unknown'}
                  </span>
                  <span className="text-xs text-[#FDF2F8]/50">
                    {merge.merged_at ? new Date(merge.merged_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    }) : 'Unknown date'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-green-400 bg-green-500/10 px-2 py-0.5 rounded text-xs">
                    Kept: {merge.primary_account?.email}
                  </span>
                  <ArrowRight className="w-3 h-3 text-[#FDF2F8]/30" />
                  <span className="text-[#FDF2F8]/60 text-xs">
                    {merge.merged_accounts?.length || 0} account(s) merged
                  </span>
                </div>
                {merge.total_entries_transferred > 0 && (
                  <div className="mt-2 text-xs text-[#0FFFE2] flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {merge.total_entries_transferred} journal entries transferred
                  </div>
                )}
                {merge.merged_accounts?.length > 0 && (
                  <div className="mt-2 text-xs text-[#FDF2F8]/40">
                    Merged: {merge.merged_accounts.map(a => a.email).join(', ')}
                  </div>
                )}
                {merge.was_user_initiated && (
                  <div className="mt-1 text-xs text-green-400/70 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    User confirmed via email
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Merged Accounts Detail Section */}
      {showMergedAccounts && (
        <div className="bg-[#1A0326] border border-[#0FFFE2]/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-semibold flex items-center gap-2">
              <Merge className="w-5 h-5 text-[#0FFFE2]" />
              Merged Accounts Detail
              <span className="text-xs text-[#0FFFE2]/60 ml-2">
                (These accounts no longer count toward Total Users)
              </span>
            </h4>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowMergedAccounts(false)}
              className="text-white/60 hover:text-white"
            >
              Close
            </Button>
          </div>
          
          {loadingMergedAccounts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#0FFFE2]" />
              <span className="ml-2 text-white/60">Loading merged accounts...</span>
            </div>
          ) : mergedAccountsList.length === 0 ? (
            <div className="text-center py-8 text-white/40">
              No merged accounts found
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-12 gap-2 text-xs text-[#FDF2F8]/50 font-medium pb-2 border-b border-[#4A0A66]/30 sticky top-0 bg-[#1A0326]">
                <div className="col-span-3">Merged Account</div>
                <div className="col-span-3">Merged Into (Primary)</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Merged At</div>
                <div className="col-span-2">Actions</div>
              </div>
              {mergedAccountsList.map((account, idx) => (
                <div 
                  key={idx} 
                  className="grid grid-cols-12 gap-2 text-sm py-2 border-b border-[#4A0A66]/20 hover:bg-[#4A0A66]/10 items-center"
                >
                  <div className="col-span-3">
                    <div className="text-white/80 font-medium truncate">{account.name || 'Unknown'}</div>
                    <div className="text-xs text-red-400/70 truncate">{account.email}</div>
                  </div>
                  <div className="col-span-3">
                    <div className="text-white/80 font-medium truncate">{account.primary_name || 'Unknown'}</div>
                    <div className="text-xs text-green-400/70 truncate">{account.primary_email}</div>
                  </div>
                  <div className="col-span-2">
                    {account.is_revoked ? (
                      <span className="text-xs bg-[#0FFFE2]/20 text-[#0FFFE2] px-2 py-0.5 rounded">
                        Revoked
                      </span>
                    ) : (
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                        Pending Fix
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 text-xs text-white/50">
                    {account.revoked_at 
                      ? new Date(account.revoked_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'N/A'
                    }
                  </div>
                  <div className="col-span-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendMergedEntriesToUser(account.user_id, account.email)}
                      disabled={sendingEntries === account.user_id}
                      className="text-xs bg-[#FF7901]/20 border-[#FF7901]/50 text-[#FF7901] hover:bg-[#FF7901]/30 px-2 py-1 h-auto"
                      title="Send journal entries from this merged account to the primary account holder via email"
                    >
                      {sendingEntries === account.user_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <FileText className="w-3 h-3 mr-1" />
                          Send Entries
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <p className="text-amber-200 text-sm font-medium">How Duplicate Account Merging Works</p>
            <p className="text-amber-200/70 text-sm mt-1">
              1. Click &ldquo;Email All&rdquo; to notify users about duplicate accounts<br/>
              2. Users click &ldquo;Keep This Account&rdquo; in the email to confirm their primary account<br/>
              3. Once confirmed, click &ldquo;Merge Now&rdquo; to transfer all journal entries and remove duplicates
            </p>
          </div>
        </div>
      </div>

      {/* Duplicate Groups */}
      {duplicateGroups.length === 0 ? (
        <div className="text-center py-12 text-white/40">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No duplicate accounts found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {duplicateGroups.map((group, idx) => {
            const hasConfirmed = group.users.some(u => u.duplicate_account_confirmed);
            
            return (
              <div 
                key={idx}
                className={`bg-[#1A0326] border rounded-xl overflow-hidden ${
                  hasConfirmed 
                    ? 'border-green-500/50 ring-1 ring-green-500/20' 
                    : 'border-[#4A0A66]'
                }`}
              >
                {/* Group Header */}
                <div 
                  className={`flex items-center justify-between p-4 cursor-pointer ${
                    hasConfirmed ? 'hover:bg-green-500/10' : 'hover:bg-[#4A0A66]/20'
                  }`}
                  onClick={() => setExpandedGroup(expandedGroup === idx ? null : idx)}
                >
                  <div className="flex items-center gap-3">
                    {expandedGroup === idx ? (
                      <ChevronDown className="w-5 h-5 text-white/60" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-white/60" />
                    )}
                    <div>
                      <span className="text-white font-medium">{group.name}</span>
                      <span className="ml-3 text-sm text-[#df1283]">
                        {group.count} accounts
                      </span>
                      {hasConfirmed && (
                        <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded animate-pulse">
                          ✓ User Confirmed
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {hasConfirmed ? (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMerge(group.name, group.users);
                        }}
                        disabled={merging === group.name}
                      >
                        {merging === group.name ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Merge className="w-4 h-4 mr-1" />
                        )}
                        Merge Now
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#FF7901] text-[#FF7901] hover:bg-[#FF7901]/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendEmails(group.name, group.users);
                        }}
                        disabled={sendingEmail === group.name}
                      >
                        {sendingEmail === group.name ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4 mr-1" />
                        )}
                        Email All
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expanded User List */}
                {expandedGroup === idx && (
                  <div className="border-t border-[#4A0A66] p-4 bg-[#4A0A66]/10">
                    <div className="space-y-3">
                      {group.users.map((user, userIdx) => (
                        <div 
                          key={user.user_id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            user.duplicate_account_confirmed
                              ? 'bg-green-500/10 border-green-500/50 ring-1 ring-green-500/20'
                              : selectedPrimary[group.name] === user.user_id
                              ? 'bg-green-500/10 border-green-500/50'
                              : 'bg-[#1A0326] border-[#4A0A66]/50 hover:border-[#4A0A66]'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#df1283]/20 flex items-center justify-center overflow-hidden">
                              {user.avatar ? (
                                <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-[#df1283] font-semibold">
                                  {(user.first_name || 'U')[0].toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-white font-medium">{user.email}</span>
                                {userIdx === 0 && (
                                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                                    Oldest
                                  </span>
                                )}
                                {user.duplicate_account_notified && !user.duplicate_account_confirmed && (
                                  <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                                    Emailed
                                  </span>
                                )}
                                {user.duplicate_account_confirmed && (
                                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded font-medium">
                                    ✓ CONFIRMED PRIMARY
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-xs text-white/50 mt-1">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {user.created_at 
                                    ? formatDistanceToNow(new Date(user.created_at), { addSuffix: true })
                                    : 'Unknown'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Shield className="w-3 h-3" />
                                  {user.auth_type || 'email'}
                                </span>
                                {user.membership_tier && (
                                  <span className="text-[#FF7901]">
                                    {user.membership_tier.replace('_', ' ')}
                                  </span>
                                )}
                                <span className={user.journal_entry_count > 0 ? 'text-[#0FFFE2]' : ''}>
                                  {user.journal_entry_count || 0} entries
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {user.duplicate_account_confirmed ? (
                              <div className="text-green-400 text-sm font-medium px-3 py-1.5 bg-green-500/20 rounded-lg">
                                Will Keep This Account
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant={selectedPrimary[group.name] === user.user_id ? "default" : "outline"}
                                className={selectedPrimary[group.name] === user.user_id 
                                  ? "bg-green-600 hover:bg-green-700"
                                  : "border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
                                }
                                onClick={() => setSelectedPrimary({
                                  ...selectedPrimary,
                                  [group.name]: user.user_id
                                })}
                              >
                                <UserCheck className="w-4 h-4 mr-1" />
                                {selectedPrimary[group.name] === user.user_id ? 'Primary' : 'Set Primary'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Merge Action */}
                    {selectedPrimary[group.name] && (
                      <div className="mt-4 pt-4 border-t border-[#4A0A66] flex items-center justify-between">
                        <p className="text-sm text-white/60">
                          <strong className="text-[#0FFFE2]">Merging will:</strong> Transfer all journal entries to the primary account and soft-delete the duplicate accounts.
                        </p>
                        <Button
                          onClick={() => handleMerge(group.name, group.users)}
                          disabled={merging === group.name}
                          className="bg-[#df1283] hover:bg-[#df1283]/80"
                        >
                          {merging === group.name ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Merge className="w-4 h-4 mr-2" />
                          )}
                          Merge & Transfer Entries
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Merge Confirmation Dialog */}
      <ConfirmDialog
        isOpen={mergeConfirmDialog.isOpen}
        onClose={() => setMergeConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={executeMerge}
        title="Confirm Account Merge"
        message={
          mergeConfirmDialog.primaryUser ? (
            <div className="space-y-3">
              <p className="text-[#FDF2F8]/80">This action will:</p>
              <ul className="list-disc list-inside space-y-1 text-[#FDF2F8]/70 text-sm">
                <li>
                  <span className="text-[#0FFFE2]">Keep:</span> {mergeConfirmDialog.primaryUser.email}
                </li>
                <li>
                  <span className="text-[#FF7901]">Merge:</span> {mergeConfirmDialog.duplicateIds.length} duplicate account(s)
                </li>
                <li>
                  <span className="text-[#df1283]">Transfer:</span> {mergeConfirmDialog.totalEntries} journal entries
                </li>
              </ul>
              <p className="text-red-400 text-sm font-medium pt-2 border-t border-[#4A0A66]">
                ⚠️ This action cannot be undone.
              </p>
            </div>
          ) : ''
        }
        confirmText="Merge Accounts"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </div>
  );
};

export default DuplicateAccountsManager;
